import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCCustomerApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer.api-service';
import {CpmCCustomerApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer.api-model';
import {
    CpmCCustomerCreateApiSchema,
    CpmCCustomerUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-common/cpmc-customer.api-schemas';
import {CpmCOO8ConnectorApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-oo8-connector.api-model';
import {CpmCOO8ConnectorApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-oo8-connector.api-service';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {CpmCS3BucketApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-s3-bucket.api-model';
import {CpmCS3BucketApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-s3-bucket.api-service';

export class NewEditCustomerModalConfig {
    constructor(public existing?: CpmCCustomerApiModel) {}
}

export class NewEditCustomerModalResult {
    constructor(public item?: CpmCCustomerApiModel) {}
}

/**
 * New/Edit Customer modal
 */
@Component({
    selector: 'app-new-edit-customer-modal',
    templateUrl: './new-edit-customer-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditCustomerModalComponent extends IBaseCRUModalComponent<
    CpmCCustomerApiModel,
    NewEditCustomerModalComponent,
    NewEditCustomerModalConfig,
    NewEditCustomerModalResult
> {
    protected _item?: CpmCCustomerApiModel;
    protected _oo8Connectors: CpmCOO8ConnectorApiModel[] = [];
    protected _s3Buckets: CpmCS3BucketApiModel[] = [];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('CustomerApiModel', (model: CpmCCustomerApiModel) => model.name ?? '<Unknown customer>');
        this.config.width = '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, []),
            oo8_connector_uuid: new FormControl<string | null>(null, []),
            s3_bucket_uuid: new FormControl<string | null>(null, [Validators.required])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading OO8 connectors...',
                f: async (): Promise<void> => {
                    this._oo8Connectors = (await firstValueFrom(CpmCOO8ConnectorApiService.getOO8ConnectorList())).data;
                }
            },
            {
                m: 'Loading S3 buckets...',
                f: async (): Promise<void> => {
                    this._s3Buckets = (await firstValueFrom(CpmCS3BucketApiService.getS3BucketList())).data;
                }
            },
            {
                m: 'Loading the customer...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this._item = await firstValueFrom(
                        CpmCCustomerApiService.getCustomerByUUID(this.config.data?.extended?.existing?.uuid ?? '')
                    );
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the customer...';

            if (this.dialogOpenMode === EDialogOpenMode.EDIT) {
                this.form.controls['s3_bucket_uuid'].disable();
            }

            this.form.patchValue(this._item!);
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCCustomerCreateApiSchema | CpmCCustomerUpdateApiSchema = this.isCreationMode
            ? {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value,
                  oo8_connector_uuid: this.form.controls['oo8_connector_uuid'].value,
                  s3_bucket_uuid: this.form.controls['s3_bucket_uuid'].value
              }
            : {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value,
                  oo8_connector_uuid: this.form.controls['oo8_connector_uuid'].value
              };

        await this.submitCU(
            null,
            CpmCCustomerApiService.createCustomer,
            CpmCCustomerApiService.updateCustomer,
            this._item?.uuid,
            toSubmit,
            (resultCRUD: CpmCCustomerApiModel) => new NewEditCustomerModalResult(resultCRUD)
        );
    }
}
